import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AutocompleteSelect } from '@/components/ui/autocomplete-select';
import { otrosTramitesService } from '@/otros-tramites/services/otrosTramitesService';
import type { UserOption } from '@/otros-tramites/services/otrosTramitesService';

interface AssignedUserSelectProps {
  value: UserOption | null;
  onChange: (value: UserOption | null) => void;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
  filterOption?: (user: UserOption) => boolean;
}

export function AssignedUserSelect({
  value,
  onChange,
  placeholder = 'Seleccioná un usuario',
  disabled = false,
  ariaLabel,
  className,
  filterOption,
}: AssignedUserSelectProps) {
  const [searchTerm, setSearchTerm] = useState('');

  const usersQuery = useQuery({
    queryKey: ['assigned-users', searchTerm],
    queryFn: () =>
      otrosTramitesService.searchUsers({
        search: searchTerm.trim(),
        per_page: 10,
      }),
    enabled: true,
    staleTime: 1000 * 30,
  });

  const options = useMemo(() => {
    const unique = new Map<string, UserOption>();

    if (value) {
      unique.set(value.id, value);
    }

    (usersQuery.data ?? [])
      .filter((user) => filterOption ? filterOption(user) : true)
      .forEach((user) => {
        if (!unique.has(user.id)) {
          unique.set(user.id, user);
        }
      });

    return Array.from(unique.values());
  }, [filterOption, usersQuery.data, value]);

  return (
    <AutocompleteSelect<UserOption>
      className={className}
      value={value}
      onChange={onChange}
      onClear={() => onChange(null)}
      options={options}
      getOptionLabel={(option) => option.email ? `${option.nombre} | ${option.email}` : option.nombre}
      placeholder={placeholder}
      ariaLabel={ariaLabel ?? placeholder}
      searchInputAriaLabel={`Buscar ${ariaLabel ?? placeholder.toLowerCase()}`}
      inputValue={searchTerm}
      onInputValueChange={setSearchTerm}
      isLoading={usersQuery.isFetching}
      emptyMessage="No encontramos usuarios con ese criterio."
      disabled={disabled}
    />
  );
}
